import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Player, Controls } from "@lottiefiles/react-lottie-player";
import cn from "./style.module.scss";
import { ContentDropdown } from "@learningblocks/shared-components";
import { PlainText } from "@learningblocks/storybook-components";
import { HighlightProvider as SharedHighlightProvider } from "@learningblocks/shared-components";
import { useHighlight } from "@learningblocks/shared/glossary";

export function FullWidthAnimation(props) {
  const player = useRef();
  const playerWrapper = useRef();
  const playerVisible = useOnScreen(playerWrapper);
  const { onPlainTextClick } =
    React.useContext(SharedHighlightProvider.Context) || useHighlight();

  const [playerRun, setPlayerRun] = useState(false);
  const [stopped, setStopped] = useState(true);

  const stop = () => {
    setStopped(true);
  };
  const play = () => {
    setStopped(false);
  };

  useEffect(() => {
    if (playerVisible && player.current && !playerRun) {
      player.current.play();
      setPlayerRun(true);
    }
  }, [playerVisible, playerRun]);

  const className = `${cn.fullWidthAnimation} ${props.className} notebook-animation-component`;

  if (props.caption) {
    return (
      <figure className={className}>
        <Animation
          playerWrapper={playerWrapper}
          altText={props.altText}
          close={props.close}
          animation={props.animation}
          stopped={stopped}
          player={player}
          play={play}
          stop={stop}
        />
        <figcaption>
          <PlainText text={props.caption} onClickHighlight={onPlainTextClick} />
        </figcaption>
        {props.contentDropdown !== undefined ? (
          <ContentDropdown contentDetails={props.contentDropdown} />
        ) : null}
      </figure>
    );
  } else {
    return (
      <Animation
        playerWrapper={playerWrapper}
        altText={props.altText}
        animation={props.animation}
        stopped={stopped}
        player={player}
      />
    );
  }
}

FullWidthAnimation.propTypes = {
  type: PropTypes.string,
  content: PropTypes.array,
  animation: PropTypes.string.isRequired,
  className: PropTypes.string,
  caption: PropTypes.string,
  getGlossaryItem: PropTypes.function,
  onOpenGlossary: PropTypes.function,
  close: PropTypes.function,
};

export function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = new IntersectionObserver(
    ([entry]) => setIntersecting(entry.isIntersecting),
    { rootMargin: "-50% 0%" }
  );

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
      // Remove the observer as soon as the component is unmounted
      return () => {
        observer.disconnect();
      };
    }
  }, [ref.current]);

  return isIntersecting;
}

export function Animation(props) {
  return (
    <div
      ref={props.playerWrapper}
      aria-label={props.altText}
      className={cn.playerWrapper}
    >
      <div
        className={props.stopped ? cn.stopped : cn.playing}
        role="button"
        onClick={() => {
          props.player.current.play();
        }}
        onKeyDown={() => {
          props.player.current.play();
        }}
        tabIndex="0"
      >
        <button className={cn.playBtn}></button>
      </div>
      <Player
        ref={props.player}
        onEvent={(event) => {
          if (event === "play") props.play();
          if (event === "stop") props.stop();
          if (event === "complete") {
            props.stop();
            props.close && props.close();
          }
        }}
        loop={false}
        autoplay={false}
        controls
        src={props.animation}
      >
        <Controls visible={true} buttons={["play", "stop"]} />
      </Player>
    </div>
  );
}
